import React, { useEffect, Suspense, lazy } from "react";
import { useLocation, useNavigate, HashRouter, Routes, Route } from "react-router-dom";
import Loader from "./utils/Loader";
import "./App.css";

// static pages (Code-Split via React.lazy)
const TextileHorizon = lazy(() => import("./screens/static/textileHorizon"));
const Index = lazy(() => import("./screens/cms/index"));
const AboutItme = lazy(() => import("./screens/static/aboutItme"));
const AboutOrganizer = lazy(() => import("./screens/static/aboutOrganizer"));
const AmentiesLayout = lazy(() => import("./screens/static/amentiesLayout"));
const AssociatesPartners = lazy(() => import("./screens/static/associatesPartners"));
const Awards = lazy(() => import("./screens/static/awards.js"));
const Contact = lazy(() => import("./screens/static/contact"));
const CookiePolicy = lazy(() => import("./screens/static/cookiePolicy"));
const DaywiseItinerary = lazy(() => import("./screens/static/daywiseItinerary"));
const EDirectory = lazy(() => import("./screens/static/eDirectory"));
const ExhibitorList2026 = lazy(() => import("./screens/static/exhibitorList2026"));
const ExhibitorsList = lazy(() => import("./screens/static/exhibitorsList"));
const ExhibitorsTravelAssistance = lazy(() => import("./screens/static/exhibitorsTravelAssistance"));
const GlanceItme = lazy(() => import("./screens/static/glanceItme"));
const GoodwillMessage = lazy(() => import("./screens/static/goodwillMessage"));
const Index1 = lazy(() => import("./screens/static/index1"));
const IndexComingSoon = lazy(() => import("./screens/static/indexComingSoon"));
const IndexOfProduct = lazy(() => import("./screens/static/indexOfProduct"));
const IndustryInfo = lazy(() => import("./screens/static/industryInfo"));
const InvitationLetter = lazy(() => import("./screens/static/invitationLetter"));
const LegalStuff = lazy(() => import("./screens/static/legalStuff"));
const MediaCoverage = lazy(() => import("./screens/static/mediaCoverage"));
const MediaKit = lazy(() => import("./screens/static/mediaKit"));
const PastEvents = lazy(() => import("./screens/static/pastEvents"));
const PhotoGallery = lazy(() => import("./screens/static/photoGallery"));
const PressRelease = lazy(() => import("./screens/static/pressRelease"));
const PrivacyPolicy = lazy(() => import("./screens/static/privacyPolicy"));
const ProductLaunch = lazy(() => import("./screens/static/productLaunch"));
const ShuttleBusService = lazy(() => import("./screens/static/shuttleBusService"));
const TermsAndConditions = lazy(() => import("./screens/static/termsAndConditions"));
const UpcomingEvent = lazy(() => import("./screens/static/upcomingEvent"));
const TrafficePlanLayout = lazy(() => import("./screens/static/trafficePlanLayout"));
const VenueDetails = lazy(() => import("./screens/static/venueDetails"));
const VenueLayout = lazy(() => import("./screens/static/venueLayout"));
const VideoGallery = lazy(() => import("./screens/static/videoGallery"));
const VisitorGuide = lazy(() => import("./screens/static/visitorGuide"));
const VisitorPromotion = lazy(() => import("./screens/static/visitorPromotion"));
const VisitorTravelAssistance = lazy(() => import("./screens/static/visitorTravelAssistance"));
const WebAdvertisementBanner = lazy(() => import("./screens/static/webAdvertisementBanner"));

// CMS pages
const CmsHome = lazy(() => import("./screens/cms/cmsHome.js"));
const CmsHeaderMaster = lazy(() => import("./screens/cms/cmsHeaderMaster.js"));

// Auth pages
const Login = lazy(() => import("./screens/user_login_auth/login.js"));
const PageNotFound = lazy(() => import("./screens/user_login_auth/pageNotFound.js"));

// Decide from the live URL at the moment an event fires (HashRouter-aware)
const getCurrentPath = () => {
  const hash = window.location.hash.replace(/^#/, "");
  return hash || window.location.pathname || "/";
};

const isRestrictedPath = () => {
  const path = getCurrentPath();
  return !(path.startsWith("/cms") || path.startsWith("/login"));
};

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();

  // Auth guard for CMS routes
  useEffect(() => {
    const storedUser = localStorage.getItem("isVerified");
    if (location.pathname.startsWith("/cms") && !storedUser) {
      navigate("/login");
    }
  }, [location, navigate]);

  // Toggle a body class so CSS can't block selection on CMS/login pages
  useEffect(() => {
    document.body.classList.toggle("allow-interaction", !isRestrictedPath());
  }, [location]);

  // Attach listeners once on mount; check the live URL inside handlers
  useEffect(() => {
    // Public pages: block as before (bubble phase)
    const block = (e) => {
      if (isRestrictedPath()) e.preventDefault();
    };

    const isInspectCombo = (e) => {
      const key = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      return (
        e.key === "F12" ||
        (mod && e.shiftKey && ["i", "j", "c"].includes(key)) ||
        (e.metaKey && e.altKey && ["i", "j", "c", "u"].includes(key)) ||
        (mod && ["u", "s", "p"].includes(key))
      );
    };

    const disableInspect = (e) => {
      if (isRestrictedPath() && isInspectCombo(e)) e.preventDefault();
    };

    // CMS/login pages: run first and stop every other handler
    const shield = (e) => {
      if (!isRestrictedPath()) e.stopImmediatePropagation();
    };
    const shieldKeys = (e) => {
      if (!isRestrictedPath() && isInspectCombo(e)) e.stopImmediatePropagation();
    };

    const events = ["contextmenu", "copy", "cut", "paste", "selectstart", "dragstart"];

    events.forEach((ev) => window.addEventListener(ev, shield, true));
    window.addEventListener("keydown", shieldKeys, true);

    events.forEach((ev) => document.addEventListener(ev, block));
    document.addEventListener("keydown", disableInspect);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, shield, true));
      window.removeEventListener("keydown", shieldKeys, true);
      events.forEach((ev) => document.removeEventListener(ev, block));
      document.removeEventListener("keydown", disableInspect);
    };
  }, []);

  return (
    <div className="App">
      <div className="auth-inner">
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* public routes */}
            <Route exact path="/" element={<Index />} />
            <Route exact path="/:page" element={<Index />} />
            <Route exact path="/login" element={<Login />} />

            {/* CMS routes - protected */}
            <Route exact path="/cms/manage" element={<CmsHome />} />
            <Route exact path="/cms/manage/:page" element={<CmsHome />} />
            <Route exact path="/cms/header" element={<CmsHeaderMaster />} />
            <Route exact path="/TextileHorizon" element={<TextileHorizon />} />

            {/* other static routes */}
            <Route exact path="/index" element={<Index />} />
            <Route exact path="/index1" element={<Index1 />} />
            <Route exact path="/AboutItme" element={<AboutItme />} />
            <Route exact path="/AboutOrganizer" element={<AboutOrganizer />} />
            <Route exact path="/AmentiesLayout" element={<AmentiesLayout />} />
            <Route exact path="/AssociatesPartners" element={<AssociatesPartners />} />
            <Route exact path="/Awards" element={<Awards />} />
            <Route exact path="/Contact" element={<Contact />} />
            <Route exact path="/CookiePolicy" element={<CookiePolicy />} />
            <Route exact path="/DaywiseItinerary" element={<DaywiseItinerary />} />
            <Route exact path="/EDirectory" element={<EDirectory />} />
            <Route exact path="/ExhibitorList2026" element={<ExhibitorList2026 />} />
            <Route exact path="/ExhibitorsList" element={<ExhibitorsList />} />
            <Route exact path="/ExhibitorsTravelAssistance" element={<ExhibitorsTravelAssistance />} />
            <Route exact path="/GlanceItme" element={<GlanceItme />} />
            <Route exact path="/GoodwillMessage" element={<GoodwillMessage />} />
            <Route exact path="/IndexComingSoon" element={<IndexComingSoon />} />
            <Route exact path="/IndexOfProduct" element={<IndexOfProduct />} />
            <Route exact path="/IndustryInfo" element={<IndustryInfo />} />
            <Route exact path="/InvitationLetter" element={<InvitationLetter />} />
            <Route exact path="/LegalStuff" element={<LegalStuff />} />
            <Route exact path="/MediaCoverage" element={<MediaCoverage />} />
            <Route exact path="/MediaKit" element={<MediaKit />} />
            <Route exact path="/PastEvents" element={<PastEvents />} />
            <Route exact path="/PhotoGallery" element={<PhotoGallery />} />
            <Route exact path="/PressRelease" element={<PressRelease />} />
            <Route exact path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            <Route exact path="/ProductLaunch" element={<ProductLaunch />} />
            <Route exact path="/ShuttleBusService" element={<ShuttleBusService />} />
            <Route exact path="/TermsAndConditions" element={<TermsAndConditions />} />
            <Route exact path="/TrafficePlanLayout" element={<TrafficePlanLayout />} />
            <Route exact path="/UpcomingEvent" element={<UpcomingEvent />} />
            <Route exact path="/VenueDetails" element={<VenueDetails />} />
            <Route exact path="/VenueLayout" element={<VenueLayout />} />
            <Route exact path="/VideoGallery" element={<VideoGallery />} />
            <Route exact path="/VisitorGuide" element={<VisitorGuide />} />
            <Route exact path="/VisitorPromotion" element={<VisitorPromotion />} />
            <Route exact path="/VisitorTravelAssistance" element={<VisitorTravelAssistance />} />
            <Route exact path="/WebAdvertisementBanner" element={<WebAdvertisementBanner />} />

            {/* fallback */}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <AppRoutes />
    </HashRouter>
  );
}

export default App;